// composables/useFixedSequence.ts
import { ref, onUnmounted, readonly } from 'vue'

// Define the contract for our sequence array
export interface SequenceStep {
  value: number
  delay: number // Time in milliseconds to wait before moving to the next step
}

export function useFixedSequence(sequence: SequenceStep[]) {
  // Initialize with the first value in the sequence, fallback to 0 if empty
  const currentValue = ref<number>(sequence.length > 0 ? sequence[0].value : 0)

  let timeoutId: ReturnType<typeof setTimeout> | null = null
  let currentIndex = 0
  let isRunning = false

  const processStep = () => {
    if (!isRunning || sequence.length === 0) return

    const step = sequence[currentIndex]
    currentValue.value = step.value

    // Wait for the defined delay, then proceed to the next index
    timeoutId = setTimeout(() => {
      currentIndex = (currentIndex + 1) % sequence.length // Loop back to 0 at the end
      processStep()
    }, step.delay)
  }

  const start = () => {
    if (isRunning || sequence.length === 0) return
    isRunning = true
    processStep()
  }

  const stop = () => {
    isRunning = false
    if (timeoutId) {
      clearTimeout(timeoutId)
      timeoutId = null
    }
  }

  const reset = () => {
    stop()
    currentIndex = 0
    currentValue.value = sequence.length > 0 ? sequence[0].value : 0
  }

  // Prevent memory leaks when the component unmounts
  onUnmounted(stop)

  return {
    value: readonly(currentValue),
    start,
    stop,
    reset,
  }
}
